import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PawPrint from '../components/PawPrint';
import Logo from '../components/Logo';
import { useSafePostHog } from '../components/PostHogProvider';
import PublicNavbar from '../components/PublicNavbar';
import {
  ArrowRight, Check, Shield, Scan, Zap, Eye, Brain, AlertTriangle,
  ChevronRight, Upload, FileText, FlaskConical, Info, Star, ChevronDown, Camera, Sparkles
} from 'lucide-react';
const Badge = ({ children, color = 'green' }) => {
  const map = {
    green:  'bg-emerald-50  text-emerald-700  border-emerald-200',
    red:    'bg-red-50      text-red-600      border-red-200',
    yellow: 'bg-amber-50   text-amber-700    border-amber-200',
    gray:   'bg-gray-100   text-gray-500     border-gray-200',
  };
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest border px-2 py-0.5 rounded-full ${map[color]}`}>
      {children}
    </span>
  );
};

const Pill = ({ children }) => (
  <span className="inline-block text-xs font-semibold bg-[#064E3B]/8 text-[#064E3B] px-3 py-1 rounded-full">
    {children}
  </span>
);

/* ─── Product UI Mockup ────────────────────────────────────────── */
const ScanMockup = () => {
  const [phase, setPhase] = useState(0);
  const [scanningOut, setScanningOut] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    // Start idle for 1.2s before going to scanning
    const t = setTimeout(() => setPhase(1), 1200);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (phase === 1) {
      // Start progress bar animation -> 1.8s
      const p = setTimeout(() => setProgress(100), 50);
      
      // Then hold for 400ms pause (Total 2.2s), then fade out for 300ms
      const out = setTimeout(() => {
        setScanningOut(true);
        setTimeout(() => setPhase(2), 300); // Switch to phase 2 after fadeout
      }, 2200);

      return () => {
        clearTimeout(p);
        clearTimeout(out);
      };
    }
  }, [phase]);

  const ingredients = [
    { name: 'Chicken By-Product Meal', risk: 'medium', tag: 'CAUTION', note: 'Lower-quality protein filler' },
    { name: 'Corn Syrup', risk: 'high', tag: 'RISK', note: 'Linked to obesity & diabetes in cats' },
    { name: 'BHA (Preservative)', risk: 'high', tag: 'RISK', note: 'Potential carcinogen — avoid long-term' },
    { name: 'Yellow 5 (Dye)', risk: 'high', tag: 'RISK', note: 'Linked to allergic reactions in pets' },
    { name: 'Salt', risk: 'medium', tag: 'CAUTION', note: 'Excess sodium — monitor intake' },
    { name: 'Water Sufficient for Processing', risk: 'low', tag: 'SAFE', note: 'Required for processing' },
  ];

  const riskColor = r =>
    r === 'high' ? 'text-red-500 bg-red-50 border-red-100' :
    r === 'medium' ? 'text-amber-600 bg-amber-50 border-amber-100' :
    'text-emerald-600 bg-emerald-50 border-emerald-100';

  const tagColor = t =>
    t === 'RISK' ? 'red' : t === 'CAUTION' ? 'yellow' : 'green';

  return (
    <div className="relative w-full max-w-md mx-auto select-none" data-testid="hero-mockup">
      {/* Browser chrome */}
      <div className="bg-white rounded-2xl shadow-2xl shadow-[#064E3B]/12 border border-gray-200/80 overflow-hidden">
        {/* Top bar */}
        <div className="bg-gray-50 border-b border-gray-200 px-4 py-2.5 flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
          <div className="flex-1 ml-3 bg-gray-200 rounded-full h-5 flex items-center px-3">
            <span className="text-[10px] text-gray-400 font-medium truncate">purrcheck.com/scan</span>
          </div>
        </div>

        {/* Content area: idle */}
        {phase === 0 && (
          <div className="p-5 space-y-3 animate-in fade-in duration-500 ease-out fill-mode-both">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Ingredient Checker</span>
              <Pill>Free Plan</Pill>
            </div>
            <div className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center bg-gray-50/60">
              <Upload className="w-7 h-7 text-gray-300 mx-auto mb-2" strokeWidth={1.5} />
              <p className="text-sm font-semibold text-gray-400">Drop label photo or paste text…</p>
            </div>
            <div className="border border-gray-100 rounded-xl p-3 bg-gray-50/40">
              <p className="text-[11px] text-gray-400 font-mono leading-relaxed line-clamp-3">
                INGREDIENTS: Chicken by-product meal, corn syrup, water sufficient for processing, BHA, yellow 5…
              </p>
            </div>
            <button
              onClick={() => setPhase(1)}
              className="w-full min-h-[44px] bg-[#064E3B] text-white text-sm font-bold rounded-xl hover:bg-[#065f46] transition-colors"
            >
              Analyze Ingredients →
            </button>
          </div>
        )}

        {/* Scanning state */}
        {phase === 1 && (
          <div className={`p-5 flex flex-col items-center justify-center min-h-[260px] animate-in fade-in duration-300 ${scanningOut ? 'animate-out fade-out duration-300 fill-mode-forwards' : ''}`}>
            <div className="relative mb-5">
              <div className="w-14 h-14 rounded-full border-2 border-gray-100 flex items-center justify-center relative overflow-hidden bg-gray-50/50">
                <div className="absolute inset-0 bg-gradient-to-tr from-[#064E3B]/10 to-transparent animate-spin duration-[2000ms]" />
                <Scan className="w-6 h-6 text-[#064E3B] relative z-10" strokeWidth={1.5} />
              </div>
            </div>
            <p className="text-sm font-bold text-gray-800">Analyzing ingredients…</p>
            <p className="text-xs text-gray-400 mt-1">Checking against veterinary databases</p>
            <div className="mt-5 w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
              <div 
                className="h-full bg-[#064E3B] rounded-full ease-linear"
                style={{ width: `${progress}%`, transitionProperty: 'width', transitionDuration: '1.8s' }} 
              />
            </div>
          </div>
        )}

        {/* Result state */}
        {phase === 2 && (
          <div className="p-5 space-y-3">
            <div className="flex items-center justify-between animate-in fade-in slide-in-from-bottom-2 duration-500 ease-out fill-mode-both" style={{ animationDelay: '100ms' }}>
              <div>
                <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">Overall Safety Grade</p>
                <p className="text-xs text-gray-400">Salmon-Based Dry Food</p>
              </div>
              <div className="flex flex-col items-center bg-red-500 text-white w-14 h-14 rounded-xl justify-center shadow-lg shadow-red-500/30">
                <span className="text-2xl font-black leading-none">D</span>
                <span className="text-[9px] font-bold opacity-80">POOR</span>
              </div>
            </div>

            <div className="bg-red-50 border border-red-100 rounded-xl px-3 py-2.5 flex items-start gap-2 animate-in fade-in slide-in-from-bottom-2 duration-500 ease-out fill-mode-both" style={{ animationDelay: '200ms' }}>
              <AlertTriangle className="w-3.5 h-3.5 text-red-500 shrink-0 mt-0.5" strokeWidth={2.5} />
              <p className="text-[11px] text-red-700 font-semibold leading-snug">
                3 potentially harmful ingredients detected — including a synthetic preservative and artificial dye.
              </p>
            </div>

            <div className="space-y-1.5 pt-1">
              {ingredients.map((ing, i) => (
                <div key={i} className="animate-in fade-in slide-in-from-bottom-1 ease-out fill-mode-both" style={{ animationDuration: '400ms', animationDelay: `${300 + i * 100}ms` }}>
                  <IngredientRow ing={ing} riskColor={riskColor} tagColor={tagColor} />
                </div>
              ))}
            </div>

            <p className="text-[10px] text-gray-400 text-center pt-1.5 animate-in fade-in duration-500 ease-out fill-mode-both" style={{ animationDelay: '900ms' }}>
              Based on veterinary nutrition guidelines &amp; ingredient databases
            </p>
          </div>
        )}
      </div>

      {/* Floating phase indicator */}
      <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
        {[0, 1, 2].map(i => (
          <div key={i} className={`h-1 transition-all duration-300 rounded-full ${i === phase ? 'w-5 bg-[#064E3B]' : 'w-1.5 bg-[#064E3B]/20'}`} />
        ))}
      </div>
    </div>
  );
};

/* ─── Ingredient row with hover tooltip ────────────────────────── */
const IngredientRow = ({ ing, riskColor, tagColor }) => {
  const [tip, setTip] = useState(false);
  const timeoutRef = useRef(null);

  const handleMouseEnter = () => {
    timeoutRef.current = setTimeout(() => setTip(true), 150);
  };
  const handleMouseLeave = () => {
    clearTimeout(timeoutRef.current);
    setTip(false);
  };

  return (
    <div
      className={`relative flex items-center justify-between px-3 py-2 rounded-lg border transition-all cursor-default ${riskColor(ing.risk)}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="flex items-center gap-2 min-w-0">
        <Info className="w-3 h-3 shrink-0 opacity-50" strokeWidth={2} />
        <span className="text-[11px] font-semibold truncate">{ing.name}</span>
      </div>
      <Badge color={tagColor(ing.tag)}>{ing.tag}</Badge>

      {/* Tooltip */}
      {tip && (
        <div className="absolute bottom-full left-0 mb-1.5 z-30 w-52 bg-gray-900/95 text-white px-3 py-2 rounded-xl shadow-xl shadow-gray-900/10 text-[11px] leading-snug animate-in fade-in slide-in-from-bottom-1 duration-200 ease-out">
          <span className="font-bold text-[#BFDB38]">{ing.name}</span>
          <div className="mt-0.5 text-gray-200">{ing.note}</div>
          <div className="absolute bottom-0 left-4 translate-y-1 w-2.5 h-2.5 bg-gray-900/95 rotate-45" />
        </div>
      )}
    </div>
  );
};

/* ─── Product Split Dashboard Demo ─────────────────────────────── */
const DashboardDemo = () => {
  const [activeIdx, setActiveIdx] = useState(null);
  const timeoutRef = useRef(null);

  const colors = {
    safe:   { row: 'border-emerald-100 bg-emerald-50/60 hover:border-emerald-200', text: 'text-emerald-800', badge: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
    medium: { row: 'border-amber-100  bg-amber-50/60 hover:border-amber-200',   text: 'text-amber-800',   badge: 'bg-amber-100  text-amber-700', dot: 'bg-amber-500'   },
    high:   { row: 'border-red-100    bg-red-50/60 hover:border-red-200',     text: 'text-red-800',     badge: 'bg-red-100    text-red-700', dot: 'bg-red-500'     },
  };

  const rows = [
    { name: 'Deboned Chicken', risk: 'safe', label: 'SAFE', note: 'High-quality, named protein source. Excellent for obligate carnivores.' },
    { name: 'BHA / BHT (Preservative)', risk: 'high', label: 'RISK', note: 'Synthetic antioxidant — linked to potential long-term health concerns in pets.' },
    { name: 'Carrageenan', risk: 'medium', label: 'CAUTION', note: 'Thickener — linked to gut inflammation in some studies.' },
    { name: 'Taurine', risk: 'safe', label: 'SAFE', note: 'Essential amino acid — vital for heart and eye health in cats.' },
  ];

  const handleMouseEnter = (i) => {
    timeoutRef.current = setTimeout(() => setActiveIdx(i), 150);
  };
  const handleMouseLeave = () => {
    clearTimeout(timeoutRef.current);
    setActiveIdx(null);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-3xl shadow-[0_20px_50px_-12px_rgba(6,78,59,0.12)] overflow-hidden flex flex-col lg:flex-row max-w-5xl mx-auto w-full text-left">
      
      {/* Left Input Panel */}
      <div className="w-full lg:w-[40%] bg-gray-50/50 border-r border-gray-100 p-6 sm:p-8 flex flex-col gap-6">
        <div>
          <label className="text-[11px] font-bold text-gray-500 uppercase tracking-widest">Select Cat Profile</label>
          <div className="mt-2 w-full bg-white border border-gray-200 shadow-sm rounded-xl px-4 py-3 text-sm text-gray-700 flex items-center justify-between cursor-pointer">
            <span className="font-semibold">Luna (Domestic Shorthair)</span>
            <ChevronDown className="w-4 h-4 text-gray-400" />
          </div>
        </div>

        <div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
             <div>
               <label className="text-[11px] font-bold text-gray-500 uppercase tracking-widest">Brand</label>
               <input type="text" placeholder="e.g. Blue Buffalo" className="mt-1.5 w-full bg-white border border-gray-200 shadow-sm rounded-xl px-4 py-3 text-sm placeholder:text-gray-400" readOnly />
             </div>
             <div>
               <label className="text-[11px] font-bold text-gray-500 uppercase tracking-widest">Product Name</label>
               <input type="text" placeholder="e.g. Chicken & Rice" className="mt-1.5 w-full bg-white border border-gray-200 shadow-sm rounded-xl px-4 py-3 text-sm placeholder:text-gray-400" readOnly />
             </div>
          </div>
          <p className="text-[10.5px] text-gray-400 italic flex items-center gap-1.5 mt-2">
            <Sparkles className="w-3 h-3 text-[var(--tw-colors-amber-500)]" /> Leave blank to let AI auto-detect
          </p>
        </div>

        <div>
          <div className="flex p-1 bg-gray-200/50 rounded-xl mb-4">
            <div className="flex-1 text-center py-2 bg-white shadow-sm rounded-lg text-xs font-bold text-gray-800">Image Scan</div>
            <div className="flex-1 text-center py-2 rounded-lg text-xs font-semibold text-gray-500">Text Paste</div>
          </div>
          <div className="border-2 border-dashed border-gray-300 rounded-2xl p-8 flex flex-col items-center justify-center bg-white text-center cursor-pointer hover:bg-gray-50 transition-colors">
            <div className="w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center mb-4">
              <Camera className="w-6 h-6 text-[#064E3B]" />
            </div>
            <p className="text-sm font-bold text-gray-800">Click to upload label</p>
            <p className="text-xs text-gray-400 mt-1">PNG, JPG up to 5MB</p>
          </div>
        </div>

        <button className="w-full min-h-[44px] bg-[#064E3B] text-white font-bold rounded-xl hover:bg-[#065f46] transition-colors mt-auto flex items-center justify-center gap-2 shadow-lg shadow-[#064E3B]/20">
          <Scan className="w-[18px] h-[18px]" /> Analyze Image
        </button>
      </div>

      {/* Right Output Panel */}
      <div className="w-full lg:w-[60%] bg-white p-6 sm:p-8 flex flex-col gap-6">
        
        {/* Top Card: Decision Engine Style */}
        <div className="flex flex-col sm:flex-row items-start justify-between gap-6">
          <div className="flex-1">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 text-emerald-700 text-[10px] font-bold uppercase tracking-widest rounded-full border border-emerald-100 mb-3">
              <Sparkles className="w-3 h-3" /> Likelihood of Acceptance: High Chance 😸
            </div>
            <h3 className="text-2xl font-black text-gray-900 leading-tight mb-2">Indoor Cat Food Formula</h3>
            <p className="text-[15px] font-bold text-amber-700 leading-tight">
              Your cat may eat this — but it's average quality food.
            </p>
            <p className="text-[12px] font-semibold text-gray-500 mt-1 opacity-70">
              Okay occasionally, not ideal for long-term feeding.
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
             <div className="flex flex-col items-center justify-center w-[72px] h-[72px] bg-white border-2 border-emerald-100 text-emerald-800 rounded-2xl shadow-sm">
                <span className="text-[10px] font-bold opacity-50 uppercase tracking-tighter">Acceptance</span>
                <span className="text-xl font-black">High</span>
             </div>
             <div className="flex flex-col items-center justify-center w-[72px] h-[72px] bg-amber-500 text-white rounded-2xl shadow-xl shadow-amber-500/20">
                <span className="text-[10px] font-bold opacity-80 uppercase tracking-tighter">Quality</span>
                <span className="text-3xl font-black leading-none">C</span>
             </div>
          </div>
        </div>

        {/* Verdict Explanation */}
        <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 sm:p-5">
          <p className="text-sm text-gray-600 leading-relaxed">
            <span className="font-bold text-gray-900">The Truth:</span> Simba will likely finish this bowl because of the high tuna content, but the filler ingredients (corn syrup and carrageenan) make it a "junk food" choice if fed every day.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          {/* Behavioral Proof */}
          <div className="border border-emerald-100 bg-emerald-50/50 rounded-xl p-4 sm:p-5">
            <div className="flex items-center gap-2 mb-3 text-emerald-800">
              <Brain className="w-4 h-4" />
              <h4 className="text-[11px] font-bold uppercase tracking-wider">Behavioral Proof</h4>
            </div>
            <ul className="text-xs text-emerald-900/80 space-y-2 pl-4 list-disc marker:text-emerald-300">
              <li>Ingredient list includes Tuna which is commonly accepted by Simba.</li>
              <li>Past history shows 80% acceptance for similar textures.</li>
              <li>Wait for the 48-hour loop to confirm digestion.</li>
            </ul>
          </div>

          {/* Health Warning */}
          <div className="border border-red-100 bg-red-50/40 rounded-xl p-4 sm:p-5">
            <div className="flex items-center gap-2 mb-3 text-red-800">
              <AlertTriangle className="w-4 h-4" />
              <h4 className="text-[11px] font-bold uppercase tracking-wider">Health Warning</h4>
            </div>
            <p className="text-xs text-red-900/80 leading-relaxed">
              This might not be great for Simba long-term. Synthetic preservatives detected are linked to chronic inflammation.
            </p>
          </div>
        </div>

        {/* Ingredient Breakdown */}
        <div>
          <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-3">Ingredient Breakdown (Highlighted)</h4>
          <div className="space-y-2">
            {rows.map((row, i) => {
              const c = colors[row.risk];
              return (
                <div
                  key={i}
                  className={`relative flex items-center justify-between gap-3 px-4 py-3 rounded-xl border cursor-default transition-all duration-200 ease-out ${c.row} shadow-sm shadow-gray-100/50`}
                  onMouseEnter={() => handleMouseEnter(i)}
                  onMouseLeave={handleMouseLeave}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${c.dot}`} />
                    <span className={`text-[13px] font-bold truncate ${c.text}`}>{row.name}</span>
                  </div>
                  <Badge color={row.risk === 'safe' ? 'green' : row.risk === 'medium' ? 'yellow' : 'red'}>{row.label}</Badge>

                  {/* Tooltip */}
                  {activeIdx === i && (
                    <div className="absolute z-30 bottom-full left-0 mb-2 w-[280px] sm:w-[320px] bg-gray-900 text-white text-xs rounded-xl px-4 py-3 shadow-2xl leading-relaxed animate-in fade-in slide-in-from-bottom-1 duration-200 ease-out">
                      <p className="font-bold text-[#BFDB38] mb-1">{row.name}</p>
                      <p className="text-gray-200 text-[11.5px]">{row.note}</p>
                      <div className="absolute bottom-0 left-6 translate-y-1 w-2.5 h-2.5 bg-gray-900 rotate-45" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Intent Tracking Mockup */}
        <div className="bg-[#1B4332] text-white rounded-2xl p-6 mt-2 shadow-lg shadow-emerald-900/20">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <h4 className="text-base font-black flex items-center gap-2 mb-1">
                Feeding this to Simba?
              </h4>
              <p className="text-[12px] text-emerald-100/70 font-medium">
                We'll check back in 48 hours for Simba's reaction.
              </p>
            </div>
            <button className="whitespace-nowrap px-6 py-2.5 bg-[#BFDB38] text-[#1B4332] font-black text-sm rounded-xl hover:scale-105 transition-all shadow-md">
              Yes, I'll try this
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

/* ─── Main Landing Page ────────────────────────────────────────── */
const Landing = () => {
  const navigate = useNavigate();
  const posthog = useSafePostHog();

  useEffect(() => {
    posthog.capture('product_page_viewed', { page: 'landing', referrer: document.referrer || 'direct' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden w-full max-w-[100vw]" style={{ fontFamily: "'Inter', 'system-ui', sans-serif" }}>

      {/* ── Google Font ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        .hero-gradient { background: radial-gradient(ellipse 80% 60% at 50% -10%, #d1fae550 0%, transparent 70%), linear-gradient(180deg, #f0fdf4 0%, #ffffff 60%); }
        .card-hover { transition: transform 0.2s ease, box-shadow 0.2s ease; }
        .card-hover:hover { transform: translateY(-3px); box-shadow: 0 20px 40px -12px rgba(6,78,59,0.12); }
        @keyframes pulse-ring { 0%, 100% { opacity: 0.3; transform: scale(1); } 50% { opacity: 0.6; transform: scale(1.06); } }
      `}</style>

      {/* ════════════════════ NAVBAR ═══════════════════════════════ */}
      <PublicNavbar />

      {/* ════════════════════ HERO ═════════════════════════════════ */}
      <section className="hero-gradient pt-28 pb-6 px-4 sm:px-6 sm:pt-36" data-testid="hero-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-12">
            {/* Label */}
            <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-bold px-3 py-1.5 rounded-full mb-6 uppercase tracking-widest">
              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
              Stop Buying Rejected Food
            </div>

            {/* Headline */}
            <h1 className="text-3xl sm:text-5xl lg:text-[3.6rem] font-black text-gray-900 leading-[1.08] tracking-tight mb-5" data-testid="hero-heading">
              Stop wasting money on cat food your cat <span className="text-[#064E3B]">refuses to eat.</span>
            </h1>

            {/* Subheadline */}
            <p className="text-base sm:text-lg text-gray-500 leading-relaxed max-w-2xl mx-auto mb-8">
              PurrCheck predicts food acceptance and flags unhealthy ingredients before you buy. The smartest way to pick your cat's next meal.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4 mb-5">
              <button
                onClick={() => navigate('/signup')}
                data-testid="hero-cta-btn"
                className="w-full sm:w-auto min-h-[44px] flex items-center justify-center gap-2 bg-[#064E3B] text-white font-bold text-base px-7 py-3 rounded-xl hover:bg-[#065f46] transition-all hover:-translate-y-0.5 shadow-lg shadow-[#064E3B]/20"
              >
                <Scan className="w-4 h-4" strokeWidth={2.5} />
                Scan a Label Free
              </button>
              <button
                onClick={() => document.getElementById('demo-section').scrollIntoView({ behavior: 'smooth' })}
                data-testid="hero-secondary-btn"
                className="w-full sm:w-auto min-h-[44px] flex items-center justify-center gap-2 text-gray-600 font-semibold text-base px-7 py-3 rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                View Example Result <ChevronRight className="w-4 h-4" strokeWidth={2.5} />
              </button>
            </div>

            {/* Trust line */}
            <p className="text-xs text-gray-400 font-medium">
              Free to try &nbsp;·&nbsp; No credit card required
            </p>
          </div>

          {/* Hero mockup */}
          <div className="relative max-w-md mx-auto pb-8">
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white z-10 pointer-events-none" style={{ top: '70%' }} />
            <ScanMockup />
          </div>
        </div>
      </section>

      {/* ════════════════════ PROBLEM SECTION ══════════════════════ */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-[#064E3B] text-white" data-testid="problem-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl font-black tracking-tight mb-8">
            Acceptance ≠ Quality
          </h2>
          <div className="grid sm:grid-cols-2 gap-6 sm:gap-12 text-left">
            <div className="bg-white/10 p-6 rounded-2xl border border-white/20">
               <p className="text-lg leading-relaxed text-emerald-50">
                 <span className="font-bold text-white block mb-2 text-xl">The Conflict</span>
                 Just because she loves the taste doesn't mean it's good for her kidneys. And just because it’s expensive doesn't mean she’ll touch it.
               </p>
            </div>
            <div className="bg-[#BFDB38] p-6 rounded-2xl border border-[#a6c12b] shadow-xl text-[#064E3B]">
               <p className="text-lg leading-relaxed">
                 <span className="font-black block mb-2 text-xl">The Solution</span>
                 PurrCheck bridges the gap between what they <span className="underline decoration-2">want</span> and what they actually <span className="underline decoration-2">need</span>.
               </p>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════ PRODUCT DEMO ════════════════════════ */}
      <section id="demo-section" className="py-12 sm:py-20 px-4 sm:px-6 bg-gray-50/60 border-y border-gray-100 w-full overflow-hidden" data-testid="demo-section">
        <div className="max-w-6xl mx-auto w-full">
          <div className="text-center mb-8 sm:mb-12">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#064E3B] mb-3">Product Demo</p>
            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 tracking-tight">
              See Exactly What Your Cat Is Eating
            </h2>
            <p className="text-gray-500 mt-3 max-w-lg mx-auto text-[15px] sm:text-base px-2">
              Hover any ingredient to reveal its risk explanation — no jargon, just clear truth.
            </p>
          </div>

          <div className="w-full">
            <DashboardDemo />
          </div>
          <p className="text-center text-xs text-gray-400 mt-6">
            Sample analysis for packaged cat food labels
          </p>
        </div>
      </section>

      {/* ════════════════════ HOW IT WORKS ═════════════════════════ */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 bg-white w-full overflow-hidden" data-testid="how-it-works-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10 sm:mb-14">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#064E3B] mb-3">The 3-Pillar System</p>
            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 tracking-tight">
              How It Works
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              {
                icon: FileText,
                step: '01',
                title: 'The Honest Scan',
                desc: 'We don’t hide behind scientific jargon. We give you a clear Grade (A to F) with a direct "Yes/No/Maybe" verdict.',
                detail: 'No-nonsense clarity',
              },
              {
                icon: Brain,
                step: '02',
                title: 'Picky Eater Score',
                desc: 'Our system analyzes proteins and textures to predict if your cat’s unique palate will accept the food.',
                detail: 'Taste & Texture matching',
              },
              {
                icon: Zap,
                step: '03',
                title: 'The 48-Hour Feedback Loop',
                desc: 'PurrCheck learns from Simba’s real reactions. Every scan makes the system smarter for your specific cat.',
                detail: 'Personalized learning',
              },
            ].map((item, i) => (
              <div key={i} className="relative group card-hover" data-testid={`step-card-${i}`}>
                <div className="bg-white border border-gray-200 rounded-2xl p-7 h-full">
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-11 h-11 bg-[#064E3B] rounded-xl flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-white" strokeWidth={2} />
                    </div>
                    <span className="text-4xl font-black text-gray-100">{item.step}</span>
                  </div>
                  <h3 className="font-bold text-gray-900 text-base mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed mb-3">{item.desc}</p>
                  <p className="text-[11px] font-semibold text-[#064E3B] flex items-center gap-1">
                    <Check className="w-3 h-3" strokeWidth={3} /> {item.detail}
                  </p>
                </div>
                {i < 2 && (
                  <div className="hidden sm:block absolute top-1/2 -right-3 z-10 text-gray-200">
                    <ArrowRight className="w-5 h-5" strokeWidth={2} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════ SCENARIOS ═════════════════════════════ */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 bg-gray-50/40 w-full overflow-hidden" data-testid="scenarios-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10 sm:mb-14">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#064E3B] mb-3">Real-World Scenarios</p>
            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 tracking-tight">
              Don't Be Fooled By Marketing
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-10">
            {/* Scenario A */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 card-hover shadow-sm flex flex-col h-full relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1.5 bg-red-500"></div>
              <h3 className="font-bold text-gray-900 text-lg mb-3">Scenario A: The "Premium" Trap</h3>
              <p className="text-gray-600 mb-6 text-sm flex-grow">Fancy, expensive brand wrapped in beautiful packaging, but packed with hidden starches and controversial thickeners.</p>
              <div className="bg-red-50 rounded-xl p-4 border border-red-100 flex items-center justify-between">
                 <span className="font-bold text-red-800 text-sm">PurrCheck Quality Score:</span>
                 <div className="bg-red-500 text-white font-black text-lg px-3 py-1 rounded-lg">2/10</div>
              </div>
            </div>

            {/* Scenario B */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 card-hover shadow-sm flex flex-col h-full relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1.5 bg-emerald-500"></div>
              <h3 className="font-bold text-gray-900 text-lg mb-3">Scenario B: The Hidden Gem</h3>
              <p className="text-gray-600 mb-6 text-sm flex-grow">Affordable, unassuming brand found at the local store, but actually boasts incredibly high-quality, named protein sources.</p>
              <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100 flex items-center justify-between">
                 <span className="font-bold text-emerald-800 text-sm">PurrCheck Quality Score:</span>
                 <div className="bg-emerald-500 text-white font-black text-lg px-3 py-1 rounded-lg">8/10</div>
              </div>
            </div>
          </div>

          {/* Reddit Trust Banner */}
          <div className="bg-[#064E3B]/5 border border-[#064E3B]/10 rounded-2xl p-5 flex items-center justify-center text-center">
            <p className="text-[#064E3B] font-bold text-sm sm:text-base flex items-center gap-2">
              <span className="text-xl">🏆</span> Built after hundreds of Reddit discussions with frustrated cat owners.
            </p>
          </div>
        </div>
      </section>

      {/* ════════════════════ WHY PURRCHECK ════════════════════════ */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 bg-white w-full overflow-hidden" data-testid="why-purrcheck-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-16 items-center">
            {/* Left copy */}
            <div className="order-2 lg:order-1">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#064E3B] mb-4">Why cat owners choose PurrCheck</p>
              <h2 className="text-3xl sm:text-4xl font-black text-gray-900 tracking-tight mb-6 sm:mb-8">
                Clarity your cat deserves — without the guesswork.
              </h2>
              <ul className="space-y-5">
                {[
                  { title: "No more guessing what's safe",          desc: 'Stop reading confusing ingredient lists and hoping for the best.' },
                  { title: 'Clear, easy-to-understand breakdowns',  desc: 'Plain English explanations — no vet degree required.' },
                  { title: 'Built using veterinary nutrition research', desc: 'Every risk flag is grounded in peer-reviewed nutrition science.' },
                  { title: 'Designed for everyday pet owners',      desc: 'Not a nutrition course — just honest answers in seconds.' },
                ].map((b, i) => (
                  <li key={i} className="flex items-start gap-3" data-testid={`why-item-${i}`}>
                    <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-[#064E3B]" strokeWidth={3} />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 text-sm">{b.title}</p>
                      <p className="text-sm text-gray-500">{b.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: Trust analysis card */}
            <div className="order-1 lg:order-2 bg-gray-50 rounded-2xl border border-gray-200 p-5 sm:p-6 shadow-sm overflow-x-auto w-full">
              {/* Grade header */}
              <div className="flex items-center justify-between mb-5">
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Example Safe Result</p>
                  <h3 className="font-black text-gray-900 text-lg">Commercial Chicken Recipe</h3>
                  <p className="text-xs text-gray-400">Chicken recipe · 85g</p>
                </div>
                <div className="flex flex-col items-center bg-emerald-500 text-white w-16 h-16 rounded-2xl justify-center shadow-lg shadow-emerald-500/30">
                  <span className="text-3xl font-black leading-tight">A</span>
                  <span className="text-[9px] font-bold opacity-90 uppercase tracking-wider">Excellent</span>
                </div>
              </div>

              {/* Ingredient tags */}
              <div className="space-y-2 mb-5">
                {[
                  { name: 'Deboned Chicken (60%)',        t: 'SAFE',    c: 'green' },
                  { name: 'Chicken Liver (Freeze-dried)', t: 'SAFE',    c: 'green' },
                  { name: 'Chicken Bone (Ground)',        t: 'SAFE',    c: 'green' },
                  { name: 'New Zealand Kelp',             t: 'SAFE',    c: 'green' },
                  { name: 'Taurine',                      t: 'SAFE',    c: 'green' },
                ].map((row, i) => (
                  <div key={i} className="flex items-center justify-between bg-white border border-emerald-100 rounded-xl px-3.5 py-2.5">
                    <div className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                      <span className="text-sm font-medium text-gray-700">{row.name}</span>
                    </div>
                    <Badge color={row.c}>{row.t}</Badge>
                  </div>
                ))}
              </div>

              <div className="border-t border-gray-200 pt-4">
                <p className="text-[11px] text-gray-400 flex items-center gap-1.5">
                  <Shield className="w-3 h-3 shrink-0" strokeWidth={2} />
                  Based on veterinary nutrition guidelines &amp; ingredient databases
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════ FINAL CTA ════════════════════════════ */}
      <section className="pt-12 pb-12 sm:pt-20 sm:pb-16 px-4 sm:px-6 bg-gray-50/60 border-t border-gray-100" data-testid="cta-section" style={{ marginBottom: 0 }}>
        <div className="max-w-2xl mx-auto text-center">
          <div className="inline-flex w-14 h-14 items-center justify-center bg-[#064E3B] rounded-2xl mb-5 sm:mb-6 shadow-xl shadow-[#064E3B]/20">
            <PawPrint className="w-7 h-7" color="#BFDB38" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-black text-gray-900 tracking-tight mb-3">
            Start Free Today
          </h2>
          <p className="text-gray-500 mb-8 text-[15px] sm:text-base">
            Scan your first cat food label in under 60 seconds.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={() => navigate('/signup')}
              data-testid="cta-signup-btn"
              className="w-full sm:w-auto min-h-[44px] flex items-center justify-center gap-2 bg-[#064E3B] text-white font-bold text-base px-8 py-3 rounded-xl hover:bg-[#065f46] transition-all hover:-translate-y-0.5 shadow-lg shadow-[#064E3B]/20"
            >
              Create Free Account <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
            </button>
            <button
              onClick={() => navigate('/login')}
              className="w-full sm:w-auto min-h-[44px] flex items-center justify-center text-gray-600 font-semibold text-base px-8 py-3 rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              I already have an account
            </button>
          </div>

          <p className="text-xs text-gray-400 mt-5">No credit card required</p>
        </div>
      </section>

    </div>
  );
};

export default Landing;
